import { InputNumber } from "antd";
import styled from "styled-components";
import { colors } from "src/theme/colors";
import { Label, fieldFocus, fieldHeight, fieldHover } from "src/components/ui";
import { line, noSteppers, radii, text } from "src/theme/ui";

/**
 * What this form's fields are made of: a name, a field, a reading.
 *
 * They were made three times, differently, in three folders one import
 * apart: the quantity field was the product's field at 38px on a hairline
 * edge, the limit price was an antd Row 40px tall with a full-strength accent
 * border wrapped around a borderless input whose digits were also accent, and
 * the time-in-force control was a default antd Button. Three heights, three
 * edges and three type treatments, side by side in one dialog.
 *
 * So the field lives here once, and it borrows its height, its hover and its
 * focus from the design system's own field rather than restating them, which
 * is what let them drift in the first place.
 */

/**
 * What a field is called, with its clearance above the field.
 *
 * The doubled selector is not decoration: Ant Design sets `.ant-form label`
 * to 14px, which is an element-plus-class selector and so outranks a single
 * class — a label inside a Form came out four sizes larger than the same
 * label rendered as a span two fields along.
 */
export const FieldLabel = styled(Label)`
  && {
    margin-bottom: 6px;
    font-size: 11.5px;
    font-weight: 600;
    color: ${colors.gray[800]};
  }
`;

/** A figure the reader types: a share count, a limit price. */
export const NumberField = styled(InputNumber)`
  width: 100%;
  height: ${fieldHeight.field}px;
  border: 1px solid ${line.field};
  border-radius: ${radii.md}px;
  background: ${colors.white};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  ${fieldHover}
  ${fieldFocus}

  .ant-input-number-input {
    height: ${fieldHeight.field - 2}px;
    padding: 0 12px;
    ${text.numeric}
    font-size: 15px;
    font-weight: 600;
    color: ${colors.gray[900]};
  }

  .ant-input-number-input::placeholder {
    font-weight: 500;
    color: ${colors.gray[400]};
  }

  /* antd 4.16 ignores the controls prop, so the steppers have to be hidden
     in CSS or they appear on hover over a field that is 38px tall. */
  ${noSteppers}
`;

/**
 * A figure the form worked out: what the order comes to.
 *
 * The field's twin, on the field's baseline, in a panel that is plainly not
 * somewhere to type. It was a second InputNumber marked readOnly, which is a
 * control drawn as a field that then refuses every keystroke.
 */
export const Readout = styled.div`
  display: flex;
  align-items: center;
  height: ${fieldHeight.field}px;
  padding: 0 12px;
  border: 1px solid ${line.hairline};
  border-radius: ${radii.md}px;
  background: ${colors.gray[50]};
  ${text.numeric}
  font-size: 15px;
  font-weight: 600;
  color: ${colors.gray[900]};
`;
